import { useTheme } from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { MontlyTimesheetsBulkActions } from '../../../components/MontlyTimesheetsBulkActions/MontlyTimesheetsBulkActions';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { TableV1 } from '../../../components/Table/TableV1';
import { useMonthlyTimesheets } from '../../../queries/hooks/useMonthlyTimesheets';
import { userState } from '../../../state/UIState';
import { TimesheetStatus, UserRole } from '../../../types';
import { useAllTimesheetsTableColumns } from '../hooks/useAllTimesheetsTableColumns';
import { FilterSection } from './FilterSection';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../constants/routes';
import { mapTsStatuses } from '../../../utils/tsMappers';

export function AllTimesheeetsTable() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const user = useAtomValue(userState);
  const [rowSelection, setRowSelection] = useState({});

  const { isLoading: timesheetsLoading, data: timesheets } = useMonthlyTimesheets({
    userId: user?.id as number,
    role: UserRole.OP_MANAGER,
  });

  const statusMappedData = useMemo(() => {
    if (!timesheets) return [];
    return mapTsStatuses(timesheets, UserRole.OP_MANAGER);
  }, [timesheets]);

  const columns = useAllTimesheetsTableColumns();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: statusMappedData ?? [],
    columns,
    state: {
      rowSelection,
      sorting,
      columnFilters,
    },
    enableRowSelection: (row) => row.original.status === TimesheetStatus.PENDING_OP_MANAGER, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
    enableSorting: true,

    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (item) => String(item.id),
  });

  if (timesheetsLoading || !timesheets) return <TableSkeletonLoader />;

  const selectedSubmittedTimesheets = table
    .getSelectedRowModel()
    .rows.map((row) => row.original)
    .filter((row) => row.status === TimesheetStatus.PENDING_OP_MANAGER);

  const hasSelectedItems = !!selectedSubmittedTimesheets?.length;

  return (
    <div>
      {hasSelectedItems && (
        <MontlyTimesheetsBulkActions
          table={table}
          approveStatus={TimesheetStatus.PENDING_DEAL_OWNERS}
          sendToLabel='Deal Owner'
          onApprove={() => navigate(`/${ROUTES.OP_MANAGER_ALL_TIMESHEETS}`)}
        />
      )}
      <FilterSection table={table} columnFilters={columnFilters} />
      <TableV1
        table={table}
        height='auto'
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </div>
  );
}
